<script setup>
import { MagnifyingGlassIcon } from "@heroicons/vue/20/solid";
import { differenceInWeeks, getYear } from "date-fns";
import { computed, inject, nextTick, provide, reactive, ref, watch } from "vue";
import CurrentFilter from "@/components/ExperiencePage/Filters/CurrentFilter.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import Pagination from "@/components/Impersonation/Pagination.vue";
import BaseInput from "@/components/InternshipLibrary/BaseInput.vue";
import NoResultsPage from "@/components/NoResultsPage.vue";
import InternshipCard from "@/components/shared/Card/InternshipCard.vue";
import BasePaginationHeader from "@/components/shared/Pagination/BasePaginationHeader.vue";
import RightArrowIcon from "@/components/svg-icons/RightArrowIcon.vue";
import { joinArrayItems } from "@/mixins/helpers.js";
import internshipService from "@/services/internship";
import { skip } from "@/services/utils.js";
import SelectedItemCard from "../Internship/SelectedItemCard.vue";
import StickyFooter from "../StickyFooter.vue";
import InternshipStepViewData from "./InternshipStepViewData.vue";

provide("cardType", "internshipConfigurator");

// Provided by Configurator/Index.vue
const {
  internship: internshipData,
  selectedInternshipIds,
  addInternship,
  removeInternship,
  loadInternships,
} = inject("internship");
const { arrivalDetails, departureDetails, program, selectedSession } = inject("program");
const { goToNextStep, goToPreviousStep } = inject("steps");

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const state = reactive({
  showBanner: true,
  expandFilters: true,
  loading: true,
  selectedItems: [],
  tempKeyword: "",
});

const weeks = computed(() => {
  return differenceInWeeks(
    new Date(departureDetails.value.departure_date),
    new Date(arrivalDetails.value.arrival_date),
  );
});
const year = computed(() => {
  return getYear(new Date(arrivalDetails.value.arrival_date));
});
const cityCountrySession = computed(() => {
  const city = arrivalDetails.value?.arrival_city?.city_ascii ?? "";
  const country = arrivalDetails.value?.arrival_city?.country?.name ?? "";

  return { city, country };
});

const semesterItems = () => {
  const termsNoCopy = [];
  termsNoCopy.push(selectedSession.value.terms[0]);

  selectedSession.value?.terms.forEach((term) => {
    if (termsNoCopy.every((noCopy) => noCopy.session !== term.session)) termsNoCopy.push(term);
  });
  return joinArrayItems(termsNoCopy, "session");
};
const getQualifications = (academicLevels, skills, languages) => {
  const concatAcademicLevels = joinArrayItems(academicLevels, "academic_year");
  const concatSkills = joinArrayItems(skills, "value");
  const concatLanguages = joinArrayItems(languages, "proficiency_level", "value", "");

  const qualificationsCopy = [concatAcademicLevels, concatSkills, concatLanguages].filter(Boolean).join(", ");

  if (qualificationsCopy) return "Open to " + qualificationsCopy;
  else return "";
};

const normalizeInternship = (item) => {
  return {
    ...item,
    program_page: {
      id: item.id,
      name: item.title,
    },
    name: item.title,
    industry: joinArrayItems(item?.industries, "career_pathway"),
    qualifications: getQualifications(item.academic_levels, item.needed_skills, item.required_languages),
    weeks: weeks.value,
    semester: semesterItems(),
    year: year.value,
    session: {
      session_type: { name: "Internship" },
      terms: selectedSession.value?.terms ?? [],
      session_travel_details: [arrivalDetails.value],
      academic_start_date: arrivalDetails.value.arrival_date ?? "",
      academic_end_date: departureDetails.value.departure_date ?? "",
      academic_start_date_finalized: departureDetails.value.arrival_date_is_final,
      academic_end_date_finalized: departureDetails.value.departure_date_is_final,
      base_price_in_cents: selectedSession.value?.base_price_in_cents,
    },
    cityCountrySession: cityCountrySession.value,
  };
};

const normalizedInternships = computed(() => internshipData.internshipItems.items.map((i) => normalizeInternship(i)));

const selectedInternships = ref([]);

const updateSelectedInternships = async (ids) => {
  let additionalInternships = [];
  let availableInternships = [];
  const idSet = new Set(ids);
  // Ids avaible on selectedInternships
  const selectedIdsSet = new Set(selectedInternships.value.map((i) => i.id));

  // Missing ids in selectedInternships
  const missingIds = ids.filter((i) => !selectedIdsSet.has(i));
  const missingIdsSet = new Set(missingIds);

  if (missingIds.length) {
    // first get all the available items from normalizedInternships
    // if some are not there we need to load them from the internshipservice
    availableInternships = normalizedInternships.value.filter((internship) => missingIdsSet.has(internship.id));

    if (availableInternships.length !== missingIds.length) {
      const additionalInternshipIds = missingIds.filter((id) => !availableInternships.map((i) => i.id).includes(id));
      const internshipResponse = await internshipService.getMany({
        internship_ids: additionalInternshipIds,
      });

      additionalInternships = internshipResponse?.data?.data?.items?.map((i) => normalizeInternship(i)) ?? [];
    }
  }

  // combine the two lists
  selectedInternships.value = selectedInternships.value
    .filter((i) => idSet.has(i.id))
    .concat(availableInternships, additionalInternships);
};

// make a watcher on selectedInternshipIds, and if it changes, then we need to load the new internships
watch(
  selectedInternshipIds,
  () => {
    updateSelectedInternships(selectedInternshipIds.value);
  },
  { immediate: true },
);

const dismissBanner = () => {
  state.showBanner = false;
};

/** FILTERS */
const defaultFilterValues = {
  keyword: "",
  area: null,
  career: null,
};
const filters = reactive({
  keyword: "",
  area: null,
  career: null,
});
const multiselectSettings = reactive({
  areas_of_study: {
    type: "single_select",
    labelInput: "label",
    labelId: "value",
    options: [],
    active: true,
    proxy: "/api/search-filter/cip",
    selectClass: "exp-multi-select border border-indigo-base md:max-w-260p",
    customSelected: true,
    placeholder: "AREA OF STUDY",
  },
  career_interest: {
    type: "single_select",
    labelInput: "label",
    labelId: "value",
    options: [],
    active: true,
    proxy: "/api/search-filter/soc",
    selectClass: "exp-multi-select border border-indigo-base md:max-w-260p",
    customSelected: true,
    placeholder: "CAREER INTEREST",
  },
});

watch(filters, () => {
  state.loading = true;
  loadInternships(params.value);
});

const activeFiltersList = computed(() => {
  const list = [];
  for (const [key, val] of Object.entries(filters)) {
    if (Array.isArray(val)) {
      for (let i = 0; i < val.length; i++) {
        if (val[i].value) {
          val[i].propName = key;
          list.push(val[i]);
        }
      }
    } else if (val && typeof val === "object") {
      list.push(val);
    } else if (typeof val === "string" && val) {
      const obj = { label: val, value: val, propName: key };
      list.push(obj);
    }
  }
  return list;
});

const clearFilters = () => {
  state.tempKeyword = "";
  filters.keyword = "";
  filters.area = "";
  filters.career = "";
};
const removeOneFilter = (obj) => {
  filters[obj.propName] = defaultFilterValues[obj.propName];
};
const handledExpandFilters = () => {
  state.expandFilters = !state.expandFilters;
};
const applyKeyword = () => {
  filters.keyword = state.tempKeyword;
};

/** PAGINATION */
const pagination = reactive({
  currentPage: 1,
  totalPages: 0,
  itemsPerPage: 10,
});
const updateCurrentPage = (page) => {
  state.loading = true;
  pagination.currentPage = page;
  loadInternships(params.value);
};

/** INTERNSHIP */
const internships = reactive({
  items: [],
  count: 0,
  end: 0,
  start: 0,
});

const params = computed(() => {
  return {
    program: { ...program, programSession: selectedSession.value },
    limit: pagination.itemsPerPage,
    skip: skip(pagination.currentPage, pagination.itemsPerPage),
    status: "Active",
    q: filters.keyword,
    major: filters.area?.label ?? "",
    career_area: filters.career?.label ?? "",
  };
});

const handleSelectButtonClick = (internshipId) => {
  if (selectedInternshipIds.value.includes(internshipId)) {
    removeInternship(internshipId);
    return;
  }

  addInternship(internshipId);
};

const bypassValidation = () => {
  addInternship("bypassId");
};

const hasBypassId = computed(() => {
  return selectedInternships.value.some((item) => item.id === "bypassId");
});

const disabledNext = computed(() => {
  return Boolean(selectedInternships.value.length < internshipData.requiredItems);
});

const disabledApply = computed(() => {
  return Boolean(selectedInternships.value.length === internshipData.maxItems);
});

const viewInternshipData = reactive({
  active: false,
  internshipItem: {},
});
const handleViewButton = (item) => {
  viewInternshipData.active = true;
  viewInternshipData.internshipItem = item;
  scrollToTop();
};
const handleBackButton = () => {
  viewInternshipData.active = false;
  viewInternshipData.internshipItem = {};
  scrollToTop();
};

const showPagination = computed(() => {
  return Boolean(!viewInternshipData.active && pagination.totalPages > 1);
});

const showFilter = computed(() => {
  return !viewInternshipData.active;
});

watch(
  () => selectedSession.value?.id,
  () => {
    state.loading = true;
    loadInternships(params.value);
  },
);

watch(
  () => internshipData.internshipItems,
  (newInternships) => {
    internships.items = newInternships?.items ?? [];
    internships.count = newInternships.count;
    internships.end = newInternships.end;
    internships.start = newInternships.start;

    pagination.totalPages = Math.ceil(internshipData.internships.count / pagination.itemsPerPage);
    internships.count = newInternships.count;
    internships.end = newInternships.end;
    internships.start = newInternships.start;

    pagination.totalPages = Math.ceil(newInternships.count / pagination.itemsPerPage);
    state.loading = internshipData.loading;
  },
  { immediate: true, deep: true },
);

// watcher for currentPage, this way when currentPage changes, we scroll to the top
watch(
  () => pagination.currentPage,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      nextTick(() => {
        scrollToTop();
      });
    }
  },
);
</script>

<template>
  <div class="bg-[#F9FAFB]">
    <div v-if="showFilter" class="font-montserrat text-center bg-gray-75 w-full py-6 md:flex gap-4 p-4">
      <div class="md:hidden pb-4 flex">
        <button
          aria-controls="expand-filters"
          :aria-expanded="state.expandFilters"
          class="flex items-center text-lg w-full justify-between text-indigo-base font-semibold"
          @click="handledExpandFilters"
        >
          <span class="flex items-center ml-1 text-base tracking-widest text-left uppercase">
            <svg
              width="42"
              height="31"
              class="mr-3.5"
              viewBox="0 0 42 31"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M40.708 3.94616H37.3016C37.0453 2.68058 36.2185 1.60611 35.0629 1.03654C33.9076 0.466987 32.5542 0.466987 31.3985 1.03654C30.2431 1.60609 29.4161 2.68058 29.1601 3.94616H0.830509C0.371746 3.94616 0 4.31896 0 4.77898C0 5.23933 0.371762 5.61212 0.830509 5.61212H29.1601C29.4161 6.87739 30.2432 7.95218 31.3985 8.52174C32.5542 9.09098 33.9076 9.09098 35.0629 8.52174C36.2186 7.95219 37.0454 6.87739 37.3016 5.61212H40.708C41.1667 5.61212 41.5385 5.23933 41.5385 4.77898C41.5385 4.31894 41.1667 3.94616 40.708 3.94616ZM33.2307 7.27841C32.5698 7.27841 31.9358 7.01514 31.4686 6.54631C31.0011 6.07749 30.7385 5.44203 30.7385 4.77896C30.7385 4.1162 31.0011 3.48044 31.4686 3.01193C31.9358 2.54311 32.5698 2.27982 33.2307 2.27982C33.892 2.27982 34.5256 2.5431 34.9932 3.01193C35.4607 3.48044 35.7232 4.1162 35.7232 4.77896C35.7232 5.44203 35.4607 6.07748 34.9932 6.54631C34.5257 7.01513 33.892 7.27841 33.2307 7.27841ZM40.708 25.6066H37.3016C37.0453 24.3414 36.2185 23.2666 35.0629 22.697C33.9076 22.1278 32.5542 22.1278 31.3985 22.697C30.2431 23.2666 29.4161 24.3414 29.1601 25.6066H0.830509C0.371746 25.6066 0 25.9794 0 26.4398C0 26.8998 0.371762 27.2726 0.830509 27.2726H29.1601C29.4161 28.5382 30.2432 29.6126 31.3985 30.1822C32.5542 30.7518 33.9076 30.7518 35.0629 30.1822C36.2186 29.6127 37.0454 28.5382 37.3016 27.2726H40.708C41.1667 27.2726 41.5385 26.8998 41.5385 26.4398C41.5385 25.9794 41.1667 25.6066 40.708 25.6066ZM33.2307 28.9389C32.5698 28.9389 31.9358 28.6756 31.4686 28.2068C31.0011 27.7383 30.7385 27.1025 30.7385 26.4397C30.7385 25.7767 31.0011 25.1412 31.4686 24.6724C31.9358 24.2036 32.5698 23.9403 33.2307 23.9403C33.892 23.9403 34.5256 24.2036 34.9932 24.6724C35.4607 25.1412 35.7232 25.7767 35.7232 26.4397C35.7232 27.1025 35.4607 27.7383 34.9932 28.2068C34.5257 28.6756 33.892 28.9389 33.2307 28.9389ZM40.708 14.776H12.3783C12.1224 13.5107 11.2952 12.4363 10.14 11.8667C8.98428 11.2971 7.63088 11.2971 6.47552 11.8667C5.31984 12.4362 4.4931 13.5107 4.23683 14.776H0.830509C0.371746 14.776 0 15.1491 0 15.6091C0 16.0692 0.371762 16.4423 0.830509 16.4423H4.23683C4.49312 17.7075 5.31992 18.782 6.47552 19.3516C7.63088 19.9211 8.98428 19.9211 10.14 19.3516C11.2953 18.782 12.1224 17.7075 12.3783 16.4423H40.708C41.1667 16.4423 41.5385 16.0692 41.5385 15.6091C41.5385 15.1491 41.1667 14.776 40.708 14.776ZM8.3071 18.1082C7.64587 18.1082 7.01219 17.845 6.54466 17.3765C6.07714 16.9076 5.81459 16.2719 5.81459 15.6091C5.81459 14.9463 6.07713 14.3106 6.54466 13.8418C7.01218 13.3732 7.64586 13.11 8.3071 13.11C8.96801 13.11 9.60201 13.3732 10.0692 13.8418C10.5367 14.3106 10.7993 14.9463 10.7993 15.6091C10.7993 16.2719 10.5367 16.9076 10.0692 17.3765C9.60201 17.845 8.96801 18.1082 8.3071 18.1082Z"
                fill="currentColor"
              />
            </svg>
            Filters
          </span>
          <svg
            width="21"
            height="12"
            viewBox="0 0 21 12"
            :class="{ '-rotate-90': state.expandFilters }"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10.5 7.18488L2.56775 0L0 2.52241L10.4869 12L21 2.52241L18.4454 0L10.5 7.18488Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
      <div
        class="grid grid-quickfilter items-center justify-center font-montserrat non-italic text-base leading-5 text-indigo-base max-w-1210p"
        :class="{ hidden: !state.expandFilters }"
      >
        <div class="relative items-center w-full">
          <input
            v-model="state.tempKeyword"
            class="mr-1 w-full md:max-w-260p max-h-13 bg-white border border-indigo-base pl-4 pb-4 pt-6 font-bold uppercase text-indigo-base focus:text-indigo-base placeholder-indigo-base placeholder-opacity-50 outline-none focus:ring-0"
            placeholder="keyword"
            @keydown.enter="applyKeyword"
          />
          <button
            tabindex="0"
            class="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
            aria-label="Search"
            @click="applyKeyword"
          >
            <MagnifyingGlassIcon
              class="h-6 w-6 font-bold cursor-pointer"
              :class="[secondaryColorClassLinks]"
              aria-hidden="true"
            />
          </button>
        </div>
        <div class="flex items-center w-full">
          <BaseInput
            v-model="filters.area"
            :field-settings="multiselectSettings.areas_of_study"
            :wrap-options="true"
            type="searchable_select"
            class="w-full"
          />
        </div>
        <div class="flex items-center w-full">
          <BaseInput
            v-model="filters.career"
            :field-settings="multiselectSettings.career_interest"
            :wrap-options="true"
            type="searchable_select"
            class="w-full"
          />
        </div>
      </div>
    </div>
    <div class="m-4">
      <div v-if="showFilter">
        <BasePaginationHeader
          :start="internships.count > 0 ? internships.start + 1 : 0"
          :end="internships.end"
          :count="internships.count"
          class="mb-5"
        />
        <CurrentFilter :filters="activeFiltersList" @clear-all="clearFilters" @remove-one-filter="removeOneFilter" />
      </div>
      <div
        v-if="state.showBanner && !viewInternshipData.active"
        class="p-6 pr-12 mb-5 font-montserrat text-sm relative top-[-3rem] md:top-0"
        :class="[secondaryColor ? `bg-gray-275` : `bg-teal-light`]"
      >
        <div class="flex">
          <span class="md:ml-14 md:inline-block mt-5 md:mt-0">
            We recommend that you choose up to 3 internships to apply to. After meeting with a Career Coach, you will
            move forward with your best-fit internship. Please keep in mind that securing an internship depends on
            availability, alignment of skills, experience, and is at the discretion of the host employer. If you want
            to apply for placements with different time frames or locations then send us an email at
            <a href="mailto:support-internships@apiexperience.com" class="text-teal-900"
              >support-internships@apiexperience.com</a
            >
          </span>
          <div
            :class="`absolute top-4 left-4 md:right-4 rounded-full ${secondaryColorClassBG} text-center w-[20px] md:top-1/3`"
          >
            <span class="text-white font-courier font-semibold">!</span>
          </div>
        </div>
        <div class="absolute top-4 right-4 rounded-full text-center w-[20px] cursor-pointer" @click="dismissBanner">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M20 2.01429L17.9857 0L10 7.98571L2.01429 0L0 2.01429L7.98571 10L0 17.9857L2.01429 20L10 12.0143L17.9857 20L20 17.9857L12.0143 10L20 2.01429Z"
              fill="#1E264C"
            />
          </svg>
        </div>
      </div>
      <Spinner v-if="state.loading" class="relative h-65vh bg-blue-100" />

      <template v-else-if="internships.count">
        <div
          v-if="!viewInternshipData.active"
          class="grid grid-cols-1 1/2lg:grid-cols-2 lg:flex lg:flex-col gap-4 xl:grid xl:col-span-2"
        >
          <InternshipCard
            v-for="internshipItem in normalizedInternships"
            :key="internshipItem.id"
            :program="internshipItem"
            :show-image="false"
            :horizontal="true"
            :uppercase-name="true"
            :flex-wrap="false"
            :selected="selectedInternshipIds.includes(internshipItem.id)"
            :disabled-apply="disabledApply"
            :apply-label="selectedInternshipIds.includes(internshipItem.id) ? 'Selected' : 'Select Placement'"
          >
            <template #buttons-lg>
              <button
                :id="`card-view-link-${internshipItem.id}`"
                class="font-bold text-base uppercase flex flex-row justify-center items-center p-0 cursor-pointer"
                :class="`${secondaryColorClass}`"
                @click="handleViewButton(internshipItem)"
              >
                VIEW
                <RightArrowIcon />
              </button>
              <button
                :id="`card-apply-btn-${internshipItem.id}`"
                type="button"
                class="box-border flex flex-row justify-center items-center w-auto h-auto max-h-60p p-5 max-w-260p border-2"
                :disabled="!selectedInternshipIds.includes(internshipItem.id) && disabledApply"
                :class="[
                  selectedInternshipIds.includes(internshipItem.id)
                    ? `${secondaryColorClassBG} text-white`
                    : `${secondaryColorClassOutlinedNoHover}`,
                  !selectedInternshipIds.includes(internshipItem.id) && disabledApply
                    ? 'cursor-not-allowed bg-gray-275 text-gray-485 border-gray-485'
                    : '',
                ]"
                @click="() => handleSelectButtonClick(internshipItem.id)"
              >
                <div class="flex gap-1 font-bold uppercase text-base tracking-tight items-center">
                  <span>
                    {{ selectedInternshipIds.includes(internshipItem.id) ? "Selected" : "Select Placement" }}
                  </span>
                  <RightArrowIcon />
                </div>
              </button>
            </template>
          </InternshipCard>
        </div>
        <div v-else class="gap-10 lg:flex lg:flex-col flex-wrap">
          <InternshipStepViewData
            :program="viewInternshipData.internshipItem"
            @back-button="handleBackButton"
            @select-button="handleSelectButtonClick"
          />
        </div>
        <Pagination
          v-if="pagination.totalPages && showPagination"
          class="mt-6"
          :page="pagination.currentPage"
          :page-count="pagination.totalPages"
          :is-experience-page="true"
          @on-page-change="updateCurrentPage"
        />
      </template>

      <NoResultsPage v-else margin-top="mt-5" />
    </div>
    <StickyFooter
      max-next-width="max-w-[260px]"
      :disabled-next="disabledNext"
      :max-items="internshipData.maxItems"
      :required-items="internshipData.requiredItems"
      :items="selectedInternships.length"
      @back="goToPreviousStep"
      @next="goToNextStep"
    >
      <template #custom>
        <SelectedItemCard
          v-for="(value, index) in internshipData.maxItems"
          :id="index"
          :key="value"
          :item="selectedInternships[index] ?? {}"
          :required="Boolean(selectedInternships.length && index + 1 === internshipData.requiredItems && !hasBypassId)"
          @remove="() => removeInternship(selectedInternships[index]?.id)"
          @bypass-validation="bypassValidation"
        >
          <template #item-name>
            {{ selectedInternships[index]?.title }}
          </template>
        </SelectedItemCard>
      </template>
    </StickyFooter>
  </div>
</template>
